import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon, { ICON_TONES } from '../../components/FeatureIcon';
import MarketingScreenshot from '../../components/MarketingScreenshot';
import { CAPTURE_BASES } from '../../lib/selectedCaptures';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 1.2s ease-out, transform 1s ease-out'
            }
      }
    >
      {children}
    </div>
  );
};

const CommunityReputation: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  return (
    <section id="reputation" className="py-16 md:py-24 scroll-mt-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <AnimatedSection>
          <div className="text-center mb-10">
            <p className={eyebrow} style={{ color: dsColors[ICON_TONES.community] }}>Reputation</p>
            <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Feedback from the people who were there.</h2>
            <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
              After an event, participants can share feedback about the experience, the host, and fellow riders. Over time, those experiences contribute to the reputation shown on a rider's profile.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center mb-10">
            <MarketingScreenshot
              base={CAPTURE_BASES.friendProfile}
              alt="A rider profile in RidePulse showing a 4.0 host rating and a 4.0 rider rating"
              caption="Reputation."
              className="lg:mx-0"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5 max-w-3xl mx-auto lg:mx-0 w-full text-left">
              <div className="p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="font-semibold text-base mb-2" style={{ color: heroPrimary }}>Hosting reputation</h3>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Feedback from participants adds context to a host's event history.
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="font-semibold text-base mb-2" style={{ color: heroPrimary }}>Rider reputation</h3>
                <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Feedback from shared events adds another perspective alongside a rider's profile and participation history.
                </p>
              </div>
            </div>
          </div>
          <div className="max-w-3xl mx-auto rounded-xl p-6 flex gap-4 items-start" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
            <span
              className="flex-shrink-0 inline-flex w-10 h-10 rounded-xl items-center justify-center"
              style={{ backgroundColor: `${dsColors[ICON_TONES.safety]}1f`, color: dsColors[ICON_TONES.safety] }}
              aria-hidden="true"
            >
              <FeatureIcon name="shield" />
            </span>
            <div>
              <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Public profiles show a reputation summary rather than individual feedback. Profile settings let you choose whether that summary appears.
              </p>
              <a
                href="#profile-privacy"
                className="inline-block mt-3 font-semibold text-sm hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Your profile and reputation →
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default CommunityReputation;
